import { element as el } from '../dom.js';
import { GateParticles } from './gate-particles.js';
import { DoorScene } from '../../rendering/door/door-scene.js';
import { DELAY_MS, DOOR_MS, RUSH_MS, RUSH_START_MS } from '../../rendering/door/door-motion.js';
import { mixer } from '../../audio/audio-mixer.js';
import { offerClose, offerInstall } from '../../offline/install-cache.js';
import gemClickUrl from '../../assets/audio/sfx/gem_click.mp3';
import unlockUrl from '../../assets/audio/sfx/unlock_gate.mp3';
import grindUrl from '../../assets/audio/sfx/stone_door_grind.mp3';

export type MenuView = 'menu' | 'play' | 'credits' | 'create';

/** A stalled frame loop must not leave the door in the way. */
const FALLBACK_MS = 9000;

/** The sealed stone door and the main menu behind it: the first thing a player touches, and the click that lets music play. */
export class MainMenu {
  private readonly door: DoorScene | null;
  private opening = false;
  private waiting = false;

  constructor(private opened: () => void, openSettings: () => void, openCreator: () => void) {
    const gate = el('gate');
    // Without WebGL the door is a flat dark panel with a plain gem, and simply fades away.
    this.door = DoorScene.create(gate, el<HTMLCanvasElement>('gate-canvas'));
    if (!this.door) gate.classList.add('gate-flat');
    // The gem is touched in the scene itself. While the world prepares it is inert, so a click anywhere on the door answers with the reason instead of silence.
    gate.addEventListener('click', event => {
      if (this.waiting) this.nudge();
      else if (!this.door || this.door.hits(event.clientX, event.clientY)) this.open();
    });
    window.addEventListener('keydown', event => {
      if ((event.key === 'Enter' || event.key === ' ') && !gate.hidden && !event.repeat) { event.preventDefault(); this.open(); }
    });
    for (const [id, view] of [['menu-play', 'play'], ['menu-credits', 'credits'], ['play-back', 'menu'], ['credits-back', 'menu']] as const) {
      el(id).addEventListener('click', () => this.show(view));
    }
    el('menu-settings').addEventListener('click', openSettings);
    el('menu-create').addEventListener('click', openCreator);
    offerInstall(el<HTMLButtonElement>('menu-install'));
    offerClose(el<HTMLButtonElement>('menu-close-app'));
    offerClose(el<HTMLButtonElement>('settings-close-app'), { armed: 'Close? Press again' });
    void mixer.preloadSfx([gemClickUrl, unlockUrl, grindUrl]);
  }

  /**
   * The menu starts a little way off, soft and small, and shows through the gap as the slabs part. As the camera is drawn through the
   * doorway it closes in to full size and focus, on the same clock and easing as the push. Only the content moves: the lobby's dark
   * overlay stays put and just fades in, so its edges never show. Each piece scales about the lobby's center so they stay in
   * proportion to one another. Nothing is left on the lobby afterward.
   */
  private approach() {
    const lobby = el('lobby'), box = lobby.getBoundingClientRect();
    const settle = (animation: Animation, done?: () => void) => { animation.onfinish = () => { animation.cancel(); done?.(); }; };
    settle(lobby.animate([{ opacity: 0 }, { opacity: 1 }], { delay: DELAY_MS + DOOR_MS * .1, duration: 1100, easing: 'ease-out', fill: 'both' }));
    for (const piece of Array.from(lobby.children) as HTMLElement[]) {
      if (piece.offsetParent === null) continue; // Not shown in this view.
      const rect = piece.getBoundingClientRect();
      piece.style.transformOrigin = `${box.left + box.width / 2 - rect.left}px ${box.top + box.height / 2 - rect.top}px`;
      // The size picks up with the push but lands softly instead of stopping dead; the focus pulls in a little ahead of it.
      settle(piece.animate([{ filter: 'blur(3px)' }, { filter: 'blur(0px)' }], { delay: RUSH_START_MS, duration: RUSH_MS * .85, easing: 'cubic-bezier(.3, 0, .2, 1)', fill: 'both' }));
      settle(piece.animate([{ transform: 'scale(.74)' }, { transform: 'scale(1)' }],
        { delay: RUSH_START_MS, duration: RUSH_MS * 1.1, easing: 'cubic-bezier(.4, 0, .1, 1)', fill: 'both' }), () => { piece.style.transformOrigin = ''; });
    }
  }

  /**
   * The door stays shut until `ready` settles, so no preparation work competes with its animation. Until then the gem is dimmed
   * and ringed, the cursor shows a wait, and a caption says what is happening; when it settles the caption goes and the gem pulses to invite a touch.
   */
  waitFor(ready: Promise<unknown>) {
    const gate = el('gate'), status = el('gate-status');
    this.waiting = true; gate.classList.add('waiting'); this.door?.setWaiting(true);
    status.className = 'gate-status'; status.textContent = 'Preparing the world…'; status.hidden = false;
    void ready.then(() => {
      this.waiting = false; gate.classList.remove('waiting'); this.door?.setWaiting(false);
      status.hidden = true;
    });
  }
  /** Progress while the door is shut (ignored once it is ready). */
  setStatus(message: string) { if (this.waiting) el('gate-status').textContent = message; }
  private nudge() {
    const status = el('gate-status');
    status.classList.remove('nudge'); void status.offsetWidth; status.classList.add('nudge');
  }

  show(view: MenuView) { el('lobby').dataset.view = view; }
  get view() { return el('lobby').dataset.view as MenuView; }

  private open() {
    const gate = el('gate'), door = this.door;
    if (this.waiting) { this.nudge(); return; }
    if (this.opening || gate.classList.contains('open')) return;
    this.opening = true; el('gate-status').hidden = true;
    mixer.playReverb(gemClickUrl, .4, .5);
    gate.classList.add('open'); this.opened();
    el('lobby').inert = false;
    // The latch lets go at the touch; the grinding starts at full volume half a second after the slabs start to drag and fades out at the end.
    mixer.playReverb(unlockUrl, 0.5); setTimeout(() => mixer.playReverb(grindUrl, .35, 1, { in: 0, out: 4 }), DELAY_MS + 500);
    let settled = false;
    const finish = () => { if (settled) return; settled = true; door?.dispose(); gate.hidden = true; el<HTMLButtonElement>('menu-play').focus(); };
    if (!door) { setTimeout(finish, 600); return; }
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    // The slabs go first; then the camera is drawn through the doorway, past the lintel and sill, onto the menu.
    const { gone, entered } = door.open();
    if (!reduced) this.approach();
    if (reduced) { void entered.then(() => setTimeout(finish, 400)); return; }
    const particles = new GateParticles(gate, () => door.edges(), () => void entered.then(finish));
    // The grinding starts with the first clunk and stops once the slabs have left the screen.
    setTimeout(() => particles.burst(70, 22), DELAY_MS + DOOR_MS * .03);
    setTimeout(() => particles.burst(34, 12), DELAY_MS + DOOR_MS * .27);
    void gone.then(() => { particles.burst(0, 14); particles.finish(); });
    setTimeout(() => { particles.dispose(); finish(); }, FALLBACK_MS);
  }
}
